import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../config/api';
import AreaOptions from '../components/AreaOptions';
import PasswordInput from '../components/PasswordInput';
import PhotoCropModal from '../components/PhotoCropModal';
import {
  AGE_CATEGORY_LABELS,
  AGE_CATEGORY_OPTIONS,
  AVAILABILITY_OPTIONS,
  EMAIL_RE,
  GENDERS,
  INDIAN_MOBILE_RE,
  MIN_PASSWORD_LENGTH,
  PHOTO_MAX_BYTES,
  PINCODE_RE,
  REFERRAL_ID_RE,
  SKILL_OPTIONS,
  VOLUNTEERING_LEVELS,
  calcAgeFromBirthDate,
  isAllowedImageFile,
  normalizeMobile,
  suggestAgeCategory,
} from '../constants/registration';
import { fetchPincodeForLocality } from '../utils/pincode';
import './ApplyToJoin.css';

const STEPS = 4;
const API_APPLICATIONS = apiUrl('/applications');

function FieldLabel({ children, required }) {
  return (
    <span>
      {children}
      {required ? <span className="apply-req"> *</span> : null}
    </span>
  );
}

function ApplyToJoin() {
  const [step, setStep] = useState(1);

  // Profile photo (reuses selfie upload field)
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoConfirmed, setPhotoConfirmed] = useState(false);
  const [cropSrc, setCropSrc] = useState('');
  const [cropFileName, setCropFileName] = useState('photo.jpg');
  const [showCrop, setShowCrop] = useState(false);
  const [pendingOriginal, setPendingOriginal] = useState(null);

  // Basic
  const [name, setName] = useState('');
  const [caste, setCaste] = useState('');
  const [subCaste, setSubCaste] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsApp, setWhatsApp] = useState('');
  const [whatsAppSame, setWhatsAppSame] = useState(true);
  const [gender, setGender] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [ageCategory, setAgeCategory] = useState('');
  const [pincodeLoading, setPincodeLoading] = useState(false);

  // Current address
  const [street, setStreet] = useState('');
  const [area, setArea] = useState('');
  const [city, setCity] = useState('Ahmedabad');
  const [stateName, setStateName] = useState('Gujarat');
  const [pincode, setPincode] = useState('');
  const [country, setCountry] = useState('India');

  // Permanent
  const [sameAsCurrent, setSameAsCurrent] = useState(true);
  const [permanentStreet, setPermanentStreet] = useState('');
  const [permanentArea, setPermanentArea] = useState('');
  const [permanentCity, setPermanentCity] = useState('');
  const [permanentState, setPermanentState] = useState('');
  const [permanentPincode, setPermanentPincode] = useState('');
  const [permanentCountry, setPermanentCountry] = useState('India');

  // Volunteering
  const [volunteerDistrict, setVolunteerDistrict] = useState('');
  const [volunteerCity, setVolunteerCity] = useState('');
  const [volunteerTaluka, setVolunteerTaluka] = useState('');
  const [volunteerVillage, setVolunteerVillage] = useState('');
  const [volunteerWard, setVolunteerWard] = useState('');
  const [volunteeringCategory, setVolunteeringCategory] = useState('');
  const [volunteeringLevels, setVolunteeringLevels] = useState([]);

  // Professional
  const [jobBusiness, setJobBusiness] = useState('');
  const [education, setEducation] = useState('');
  const [availability, setAvailability] = useState('');
  const [availabilityDescription, setAvailabilityDescription] = useState('');
  const [previousExperience, setPreviousExperience] = useState('');
  const [referralId, setReferralId] = useState('');

  // Legacy optional + docs + consent
  const [skills, setSkills] = useState([]);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [consentAccepted, setConsentAccepted] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [done, setDone] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');

  const age = useMemo(() => calcAgeFromBirthDate(birthDate), [birthDate]);

  useEffect(() => {
    if (whatsAppSame) setWhatsApp(phone);
  }, [whatsAppSame, phone]);

  useEffect(() => {
    setAgeCategory(suggestAgeCategory(age, gender));
  }, [age, gender]);

  useEffect(() => {
    let cancelled = false;
    async function loadPin() {
      if (!area) return;
      setPincodeLoading(true);
      const pin = await fetchPincodeForLocality(area, { city, state: stateName });
      if (!cancelled && pin) setPincode(pin);
      if (!cancelled) setPincodeLoading(false);
    }
    loadPin();
    return () => {
      cancelled = true;
    };
  }, [area, city, stateName]);

  useEffect(() => {
    if (!volunteeringCategory && ageCategory) {
      setVolunteeringCategory(ageCategory);
    }
  }, [ageCategory, volunteeringCategory]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    };
  }, [photoPreview, cropSrc]);

  const setPhotoResult = useCallback((file, previewUrl) => {
    setPhotoFile(file);
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return previewUrl;
    });
    setPhotoConfirmed(false);
    setShowCrop(false);
    setPendingOriginal(null);
    if (cropSrc) {
      URL.revokeObjectURL(cropSrc);
      setCropSrc('');
    }
  }, [cropSrc]);

  const confirmPhoto = () => {
    if (!photoFile || !photoPreview) return;
    setPhotoConfirmed(true);
    setFieldErrors((prev) => {
      if (!prev.photo) return prev;
      const next = { ...prev };
      delete next.photo;
      return next;
    });
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setPhotoConfirmed(false);
  };

  const validatePasswordField = (value) => {
    if (!value) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.password;
        return next;
      });
      return;
    }
    if (value.length < MIN_PASSWORD_LENGTH) {
      setFieldErrors((prev) => ({
        ...prev,
        password: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
      }));
    } else {
      setFieldErrors((prev) => {
        if (!prev.password) return prev;
        const next = { ...prev };
        delete next.password;
        return next;
      });
    }
  };

  const openCropForFile = (file) => {
    if (!isAllowedImageFile(file)) {
      setFormError('Profile photo must be an image (JPG, PNG or WEBP).');
      return;
    }
    if (/\.(heic|heif)$/i.test(file.name || '') || /image\/hei[cf]/i.test(file.type || '')) {
      setFormError(
        'HEIC/HEIF photos are not supported. On iPhone, export or share as JPEG, then try again.'
      );
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setFormError('Profile photo must be 5MB or smaller.');
      return;
    }
    setFormError('');
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    const url = URL.createObjectURL(file);
    setPendingOriginal(file);
    setCropFileName(file.name || 'photo.jpg');
    setCropSrc(url);
    setShowCrop(true);
  };

  const onPhotoPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    openCropForFile(f);
  };

  const toggleSkill = (s) => {
    setSkills((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const toggleVolunteeringLevel = (level) => {
    setVolunteeringLevels((prev) =>
      prev.includes(level) ? prev.filter((x) => x !== level) : [...prev, level]
    );
  };

  const validateStep = (n) => {
    const errs = {};
    setFormError('');

    if (n === 1) {
      if (!photoFile || !photoConfirmed) errs.photo = 'Profile photo is required — preview and confirm it';
      if (!name.trim()) errs.name = 'Full name is required';
      if (!caste.trim()) errs.caste = 'Caste is required';
      if (!subCaste.trim()) errs.subCaste = 'Sub caste is required';
      if (!email.trim()) errs.email = 'Email is required';
      else if (!EMAIL_RE.test(email.trim())) errs.email = 'Enter a valid email address';
      if (password.length < MIN_PASSWORD_LENGTH) {
        errs.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
      } else if (password !== confirmPassword) {
        errs.confirmPassword = 'Passwords do not match';
      }
      const p =normalizeMobile(phone);
      const w = normalizeMobile(whatsAppSame ? phone : whatsApp);
      if (!INDIAN_MOBILE_RE.test(p)) errs.phone = 'Enter a valid 10-digit Indian mobile number';
      if (!INDIAN_MOBILE_RE.test(w)) errs.whatsApp = 'Enter a valid 10-digit WhatsApp number';
      if (!gender) errs.gender = 'Gender is required';
      if (!birthDate) errs.birthDate = 'Birth date is required';
      else if (age == null || age < 0 || age > 120) errs.birthDate = 'Enter a valid birth date';
      if (!ageCategory) errs.ageCategory = 'Age category is required';
    }

    if (n === 2) {
      if (!street.trim()) errs.street = 'Street / house no. is required';
      if (!area) errs.area = 'Area / locality is required';
      if (!city.trim()) errs.city = 'City is required';
      if (!stateName.trim()) errs.state = 'State is required';
      if (!PINCODE_RE.test(pincode.trim())) errs.pincode = 'Pincode must be 6 digits';
      if (!country.trim()) errs.country = 'Country is required';
      if (!sameAsCurrent) {
        if (!permanentStreet.trim()) errs.permanentStreet = 'Required';
        if (!permanentArea.trim()) errs.permanentArea = 'Required';
        if (!permanentCity.trim()) errs.permanentCity = 'Required';
        if (!permanentState.trim()) errs.permanentState = 'Required';
        if (!PINCODE_RE.test(permanentPincode.trim())) errs.permanentPincode = '6 digits required';
        if (!permanentCountry.trim()) errs.permanentCountry = 'Required';
      }
    }

    if (n === 3) {
      if (!volunteerDistrict.trim()) errs.volunteerDistrict = 'District is required';
      if (!volunteerCity.trim()) errs.volunteerCity = 'City is required';
      if (!volunteerTaluka.trim()) errs.volunteerTaluka = 'Taluka is required';
      if (!volunteerVillage.trim()) errs.volunteerVillage = 'Village is required';
      if (!volunteerWard.trim()) errs.volunteerWard = 'Ward is required';
      if (!volunteeringCategory) errs.volunteeringCategory = 'Volunteering category is required';
      if (!jobBusiness.trim()) errs.jobBusiness = 'Job / business is required';
      if (!education.trim()) errs.education = 'Education is required';
      if (!availability) errs.availability = 'Availability is required';
      if (!availabilityDescription.trim()) {
        errs.availabilityDescription = 'Availability description is required';
      }
      if (!previousExperience.trim()) errs.previousExperience = 'Previous experience is required';
      if (referralId.trim() && !REFERRAL_ID_RE.test(referralId.trim())) {
        errs.referralId = 'Format must be SS-10001 (SS- + 5 digits)';
      }
    }

    if (n === 4) {
      if (!aadhaarFile) errs.aadhaar = 'Aadhaar scan / photo is required';
      if (!consentAccepted) errs.consent = 'You must agree to the terms and conditions';
    }

    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setFormError(Object.values(errs)[0]);
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(STEPS, s + 1));
  };

  const back = () => {
    setFormError('');
    setFieldErrors({});
    setStep((s) => Math.max(1, s - 1));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep(4)) return;

    setSubmitting(true);
    setFormError('');
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('caste', caste.trim());
      fd.append('subCaste', subCaste.trim());
      fd.append('email', email.trim().toLowerCase());
      fd.append('password', password);
      fd.append('phone', normalizeMobile(phone));
      fd.append('whatsApp', normalizeMobile(whatsAppSame ? phone : whatsApp));
      fd.append('gender', gender);
      fd.append('birthDate', birthDate);
      fd.append('ageCategory', ageCategory);
      fd.append('street', street.trim());
      fd.append('area', area);
      fd.append('city', city.trim());
      fd.append('state', stateName.trim());
      fd.append('pincode', pincode.trim());
      fd.append('country', country.trim());
      fd.append('sameAsCurrentAddress', sameAsCurrent ? 'true' : 'false');
      fd.append('permanentStreet', sameAsCurrent ? street.trim() : permanentStreet.trim());
      fd.append('permanentArea', sameAsCurrent ? area : permanentArea.trim());
      fd.append('permanentCity', sameAsCurrent ? city.trim() : permanentCity.trim());
      fd.append('permanentState', sameAsCurrent ? stateName.trim() : permanentState.trim());
      fd.append('permanentPincode', sameAsCurrent ? pincode.trim() : permanentPincode.trim());
      fd.append('permanentCountry', sameAsCurrent ? country.trim() : permanentCountry.trim());
      fd.append('volunteerDistrict', volunteerDistrict.trim());
      fd.append('volunteerCity', volunteerCity.trim());
      fd.append('volunteerTaluka', volunteerTaluka.trim());
      fd.append('volunteerVillage', volunteerVillage.trim());
      fd.append('volunteerWard', volunteerWard.trim());
      fd.append('volunteeringCategory', volunteeringCategory);
      fd.append('volunteeringLevel', JSON.stringify(volunteeringLevels));
      fd.append('jobBusiness', jobBusiness.trim());
      fd.append('education', education.trim());
      fd.append('availability', availability);
      fd.append('availabilityDescription', availabilityDescription.trim());
      fd.append('previousExperience', previousExperience.trim());
      fd.append('referralId', referralId.trim().toUpperCase());
      fd.append('consentAccepted', 'true');
      fd.append('skills', JSON.stringify(skills));
      fd.append('selfie', photoFile);
      fd.append('aadhaar', aadhaarFile);

      const res = await fetch(API_APPLICATIONS, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFormError(data.error || 'Submission failed. Try again.');
        return;
      }
      setReferenceNumber(data.referenceNumber || '');
      setDone(true);
    } catch {
      setFormError('Network error. Check that the server is running.');
    } finally {
      setSubmitting(false);
    }
  };

  const progressClass = (i) => {
    if (i < step) return 'apply-progress-seg done';
    if (i === step) return 'apply-progress-seg active';
    return 'apply-progress-seg';
  };

  const err = (key) =>
    fieldErrors[key] ? (
      <span className="apply-field-error" role="alert">
        {fieldErrors[key]}
      </span>
    ) : null;

  if (done) {
    return (
      <div className="apply-page">
        <div className="apply-shell apply-shell-wide">
          <div className="apply-card">
            <div className="apply-success">
              <div className="apply-success-icon" aria-hidden>
                ✓
              </div>
              <h2>Application received</h2>
              <p>
                Thank you for applying to Sangram Sena. We have received your details and documents.
              </p>
              <p className="apply-success-hint">
                Sign in with your email and the password you created to check your status. Once an
                admin approves you, you get full access to your profile and ID card.
              </p>
              <p className="apply-hint">Your reference ID (for office / support)</p>
              <div className="apply-ref-box">{referenceNumber}</div>
              <div className="apply-success-actions">
                <Link className="apply-success-primary-link" to="/login">
                  Back to sign in
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="apply-page">
      <div className="apply-shell apply-shell-wide">
        <div className="apply-card">
          <div className="apply-top">
            <Link className="apply-back" to="/login">
              ← Back
            </Link>
            <div className="apply-heading">
              <h1 className="apply-title">Apply to join</h1>
              <p className="apply-step-label">
                Step {step} of {STEPS}
              </p>
            </div>
          </div>

          <div className="apply-progress" aria-hidden>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={progressClass(i)}>
                <div className="apply-progress-fill" />
              </div>
            ))}
          </div>

          <form
            onSubmit={step === 4 ? submit : (e) => e.preventDefault()}
            className="apply-form-block"
          >
            {formError ? (
              <p className="apply-error" role="alert">
                {formError}
              </p>
            ) : null}

            {step === 1 && (
              <>
                <h2 className="apply-section-title">Profile Photo</h2>
                <div className="apply-doc-row">
                  <FieldLabel required>Profile Photo</FieldLabel>
                  {!photoPreview ? (
                    <>
                      <input
                        className="apply-file-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp"
                        onChange={onPhotoPick}
                      />
                      <p className="apply-hint">
                        JPG, PNG or WEBP · max 5MB · square crop · confirm after preview. On iPhone,
                        choose &quot;Most Compatible&quot; / JPEG if the photo will not open.
                      </p>
                    </>
                  ) : null}
                  {photoPreview ? (
                    <div className={`apply-photo-confirm${photoConfirmed ? ' locked' : ' pending'}`}>
                      <img className="apply-preview-thumb lg" src={photoPreview} alt="Profile preview" />
                      <div className="apply-photo-confirm-actions">
                        {photoConfirmed ? (
                          <span className="apply-photo-ok">Photo confirmed (cannot change)</span>
                        ) : (
                          <>
                            <span className="apply-hint">Does this look correct?</span>
                            <div className="apply-photo-btns">
                              <button type="button" className="apply-btn-secondary" onClick={clearPhoto}>
                                Change photo
                              </button>
                              <button type="button" className="apply-btn-primary apply-btn-compact" onClick={confirmPhoto}>
                                Confirm photo
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ) : null}
                  {err('photo')}
                </div>

                <h2 className="apply-section-title">Basic Information</h2>
                <label className="apply-label">
                  <FieldLabel required>Full Name</FieldLabel>
                  <input
                    className="apply-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                  />
                  {err('name')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Caste</FieldLabel>
                  <input className="apply-input" value={caste} onChange={(e) => setCaste(e.target.value)} />
                  {err('caste')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Sub Caste</FieldLabel>
                  <input
                    className="apply-input"
                    value={subCaste}
                    onChange={(e) => setSubCaste(e.target.value)}
                  />
                  {err('subCaste')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Email Address</FieldLabel>
                  <input
                    className="apply-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                  {err('email')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Create Password</FieldLabel>
                  <PasswordInput
                    inputClassName="apply-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={(e) => validatePasswordField(e.target.value)}
                    autoComplete="new-password"
                    placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  />
                  <p className="apply-hint">
                    You will sign in with your email and this password to track your application.
                  </p>
                  {err('password')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Confirm Password</FieldLabel>
                  <PasswordInput
                    inputClassName="apply-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  {err('confirmPassword')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Phone Number</FieldLabel>
                  <input
                    className="apply-input"
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                    placeholder="10-digit mobile"
                  />
                  {err('phone')}
                </label>
                <label className="apply-check">
                  <input
                    type="checkbox"
                    checked={whatsAppSame}
                    onChange={(e) => setWhatsAppSame(e.target.checked)}
                  />
                  WhatsApp same as phone
                </label>
                <label className="apply-label">
                  <FieldLabel required>WhatsApp Number</FieldLabel>
                  <input
                    className="apply-input"
                    type="tel"
                    inputMode="numeric"
                    value={whatsApp}
                    onChange={(e) => {
                      setWhatsAppSame(false);
                      setWhatsApp(e.target.value);
                    }}
                    disabled={whatsAppSame}
                    autoComplete="tel"
                  />
                  {err('whatsApp')}
                </label>
                <fieldset className="apply-fieldset">
                  <legend className="apply-legend">
                    <FieldLabel required>Gender</FieldLabel>
                  </legend>
                  <div className="apply-radio-row" role="radiogroup" aria-label="Gender">
                    {GENDERS.map((g) => (
                      <label key={g} className={`apply-radio${gender === g ? ' on' : ''}`}>
                        <input
                          type="radio"
                          name="gender"
                          value={g}
                          checked={gender === g}
                          onChange={() => setGender(g)}
                        />
                        {g}
                      </label>
                    ))}
                  </div>
                  {err('gender')}
                </fieldset>
                <label className="apply-label">
                  <FieldLabel required>Birth Date</FieldLabel>
                  <input
                    className="apply-input apply-date-input"
                    type="date"
                    value={birthDate}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                  {err('birthDate')}
                </label>
                <label className="apply-label">
                  Age
                  <input
                    className="apply-input"
                    value={age == null ? '' : String(age)}
                    readOnly
                    placeholder="Auto from birth date"
                  />
                </label>
                <label className="apply-label">
                  <FieldLabel required>Age Category</FieldLabel>
                  <input
                    className="apply-input"
                    value={ageCategory ? AGE_CATEGORY_LABELS[ageCategory] || ageCategory : ''}
                    readOnly
                    placeholder="Set automatically from age and gender"
                  />
                  <p className="apply-hint">
                    Assigned automatically from your age - not editable.
                  </p>
                  {err('ageCategory')}
                </label>
              </>
            )}

            {step === 2 && (
              <>
                <h2 className="apply-section-title">Current Address</h2>
                <label className="apply-label">
                  <FieldLabel required>Country</FieldLabel>
                  <input
                    className="apply-input"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  />
                  {err('country')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>State</FieldLabel>
                  <input
                    className="apply-input"
                    value={stateName}
                    onChange={(e) => setStateName(e.target.value)}
                  />
                  {err('state')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>City</FieldLabel>
                  <input className="apply-input" value={city} onChange={(e) => setCity(e.target.value)} />
                  {err('city')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Area / Locality</FieldLabel>
                  <select
                    className="apply-select"
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                  >
                    <option value="">Select area</option>
                    <AreaOptions current={area} />
                  </select>
                  {err('area')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Street / House No.</FieldLabel>
                  <textarea
                    className="apply-textarea"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    placeholder="House no., street, landmark"
                  />
                  {err('street')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Pincode</FieldLabel>
                  <input
                    className="apply-input"
                    inputMode="numeric"
                    maxLength={6}
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={pincodeLoading ? 'Looking up…' : ''}
                  />
                  <p className="apply-hint">
                    {pincodeLoading
                      ? 'Fetching pincode for the selected area…'
                      : 'Auto-filled from area when available — you can edit it.'}
                  </p>
                  {err('pincode')}
                </label>

                <h2 className="apply-section-title">Permanent Address</h2>
                <label className="apply-check">
                  <input
                    type="checkbox"
                    checked={sameAsCurrent}
                    onChange={(e) => setSameAsCurrent(e.target.checked)}
                  />
                  Same as Current Address
                </label>
                {!sameAsCurrent ? (
                  <>
                    <label className="apply-label">
                      <FieldLabel required>Country</FieldLabel>
                      <input
                        className="apply-input"
                        value={permanentCountry}
                        onChange={(e) => setPermanentCountry(e.target.value)}
                      />
                      {err('permanentCountry')}
                    </label>
                    <label className="apply-label">
                      <FieldLabel required>State</FieldLabel>
                      <input
                        className="apply-input"
                        value={permanentState}
                        onChange={(e) => setPermanentState(e.target.value)}
                      />
                      {err('permanentState')}
                    </label>
                    <label className="apply-label">
                      <FieldLabel required>City</FieldLabel>
                      <input
                        className="apply-input"
                        value={permanentCity}
                        onChange={(e) => setPermanentCity(e.target.value)}
                      />
                      {err('permanentCity')}
                    </label>
                    <label className="apply-label">
                      <FieldLabel required>Area / Locality</FieldLabel>
                      <input
                        className="apply-input"
                        value={permanentArea}
                        onChange={(e) => setPermanentArea(e.target.value)}
                      />
                      {err('permanentArea')}
                    </label>
                    <label className="apply-label">
                      <FieldLabel required>Street / House No.</FieldLabel>
                      <textarea
                        className="apply-textarea"
                        value={permanentStreet}
                        onChange={(e) => setPermanentStreet(e.target.value)}
                      />
                      {err('permanentStreet')}
                    </label>
                    <label className="apply-label">
                      <FieldLabel required>Pincode</FieldLabel>
                      <input
                        className="apply-input"
                        inputMode="numeric"
                        maxLength={6}
                        value={permanentPincode}
                        onChange={(e) =>
                          setPermanentPincode(e.target.value.replace(/\D/g, '').slice(0, 6))
                        }
                      />
                      {err('permanentPincode')}
                    </label>
                  </>
                ) : (
                  <p className="apply-hint">Permanent address will match your current address.</p>
                )}
              </>
            )}

            {step === 3 && (
              <>
                <h2 className="apply-section-title">Volunteering Service Details</h2>
                <label className="apply-label">
                  <FieldLabel required>District</FieldLabel>
                  <input
                    className="apply-input"
                    value={volunteerDistrict}
                    onChange={(e) => setVolunteerDistrict(e.target.value)}
                  />
                  {err('volunteerDistrict')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>City</FieldLabel>
                  <input
                    className="apply-input"
                    value={volunteerCity}
                    onChange={(e) => setVolunteerCity(e.target.value)}
                  />
                  {err('volunteerCity')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Taluka</FieldLabel>
                  <input
                    className="apply-input"
                    value={volunteerTaluka}
                    onChange={(e) => setVolunteerTaluka(e.target.value)}
                  />
                  {err('volunteerTaluka')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Village</FieldLabel>
                  <input
                    className="apply-input"
                    value={volunteerVillage}
                    onChange={(e) => setVolunteerVillage(e.target.value)}
                  />
                  {err('volunteerVillage')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Ward</FieldLabel>
                  <input
                    className="apply-input"
                    value={volunteerWard}
                    onChange={(e) => setVolunteerWard(e.target.value)}
                  />
                  {err('volunteerWard')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Volunteering Category</FieldLabel>
                  <select
                    className="apply-select"
                    value={volunteeringCategory}
                    onChange={(e) => setVolunteeringCategory(e.target.value)}
                  >
                    <option value="">Select category</option>
                    {AGE_CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  {err('volunteeringCategory')}
                </label>
                <div className="apply-label">
                  <span>Volunteering Level</span>
                  <p className="apply-hint">Select one or more levels</p>
                  <div className="apply-check-grid">
                    {VOLUNTEERING_LEVELS.map((level) => (
                      <label key={level} className="apply-check">
                        <input
                          type="checkbox"
                          checked={volunteeringLevels.includes(level)}
                          onChange={() => toggleVolunteeringLevel(level)}
                        />
                        {level}
                      </label>
                    ))}
                  </div>
                </div>

                <h2 className="apply-section-title">Professional Details &amp; Reference</h2>
                <label className="apply-label">
                  <FieldLabel required>Job / Business</FieldLabel>
                  <input
                    className="apply-input"
                    value={jobBusiness}
                    onChange={(e) => setJobBusiness(e.target.value)}
                  />
                  {err('jobBusiness')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Education</FieldLabel>
                  <input
                    className="apply-input"
                    value={education}
                    onChange={(e) => setEducation(e.target.value)}
                  />
                  {err('education')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Availability</FieldLabel>
                  <select
                    className="apply-select"
                    value={availability}
                    onChange={(e) => setAvailability(e.target.value)}
                  >
                    <option value="">Select availability</option>
                    {AVAILABILITY_OPTIONS.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                  {err('availability')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Availability Description</FieldLabel>
                  <textarea
                    className="apply-textarea"
                    value={availabilityDescription}
                    onChange={(e) => setAvailabilityDescription(e.target.value)}
                    placeholder="Describe your preferred hours / days"
                  />
                  {err('availabilityDescription')}
                </label>
                <label className="apply-label">
                  <FieldLabel required>Previous Experience</FieldLabel>
                  <textarea
                    className="apply-textarea"
                    value={previousExperience}
                    onChange={(e) => setPreviousExperience(e.target.value)}
                  />
                  {err('previousExperience')}
                </label>
                <label className="apply-label">
                  Reference ID
                  <input
                    className="apply-input"
                    value={referralId}
                    onChange={(e) => setReferralId(e.target.value.toUpperCase())}
                    placeholder="SS-10001 (optional)"
                  />
                  {err('referralId')}
                </label>
              </>
            )}

            {step === 4 && (
              <>
                <h2 className="apply-section-title">Identity document</h2>
                <div className="apply-doc-row">
                  <FieldLabel required>Aadhaar (scan or photo)</FieldLabel>
                  <input
                    className="apply-file-input"
                    type="file"
                    accept="image/*,.heic,.heif"
                    onChange={(e) => setAadhaarFile(e.target.files?.[0] || null)}
                  />
                  <p className="apply-hint">
                    Kept for admin verification (existing flow). Max 5MB image.
                  </p>
                  {aadhaarFile ? (
                    <p className="apply-photo-ok">Selected: {aadhaarFile.name}</p>
                  ) : null}
                  {err('aadhaar')}
                </div>

                <div className="apply-label">
                  <span>Skills you can offer (optional)</span>
                  <div className="apply-chip-grid">
                    {SKILL_OPTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`apply-chip${skills.includes(s) ? ' on' : ''}`}
                        onClick={() => toggleSkill(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <h2 className="apply-section-title">Consent</h2>
                <label className="apply-check apply-check-consent">
                  <input
                    type="checkbox"
                    checked={consentAccepted}
                    onChange={(e) => setConsentAccepted(e.target.checked)}
                  />
                  <span>
                    I agree to the terms and conditions and confirm that all information provided is
                    accurate.
                    <span className="apply-req"> *</span>
                  </span>
                </label>
                {err('consent')}
              </>
            )}

            <div className="apply-nav-row">
              {step > 1 ? (
                <button type="button" className="apply-btn-ghost" onClick={back}>
                  Previous
                </button>
              ) : null}
              {step < STEPS ? (
                <button type="button" className="apply-btn-primary" onClick={next}>
                  Next
                </button>
              ) : (
                <button type="submit" className="apply-btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Complete Registration'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {showCrop && cropSrc ? (
        <PhotoCropModal
          imageUrl={cropSrc}
          fileName={cropFileName}
          onClose={() => {
            setShowCrop(false);
            if (cropSrc) URL.revokeObjectURL(cropSrc);
            setCropSrc('');
            setPendingOriginal(null);
          }}
          onUseOriginal={() => {
            if (!pendingOriginal) return;
            const url = URL.createObjectURL(pendingOriginal);
            setPhotoResult(pendingOriginal, url);
          }}
          onApply={(file, previewUrl) => setPhotoResult(file, previewUrl)}
        />
      ) : null}
    </div>
  );
}

export default ApplyToJoin;
